import { lineString } from '@turf/turf';
import type { Map as MlMap } from 'maplibre-gl';
import type { Feature, LineString, MultiLineString, Polygon, MultiPolygon } from 'geojson';
import type { LngLat } from '../../shared/protocol';
import { BARRIER_KINDS, TIER_CLASSES, roadClassesFor, type BarrierKind } from '../../shared/travelMode';
import { RAIL_CLASSES, isBarrierFeature, shorelineChains } from '../../shared/map/barrierClasses';
import type { ClassifiedBarrier } from '../../shared/map/boundarySelector';
import type { NamedLine } from '../../shared/map/boundaryDescribe';
import type { BarrierLine } from './autoBoundary';
import { windowBbox } from './autoBoundary';

/**
 * v23 BOLT-124 — the tile-feature harvest, as a REUSABLE layer.
 *
 * We already pull road features out of the vector tiles we render (portal
 * placement reads `transportation` and its `class`). This generalises that into
 * "give me the barriers around here", adding water edges and rail, and it is
 * deliberately written as a layer rather than a one-off because Landmarks (v24)
 * reuses it for parks, water, schools and so on. No Overpass, no new data
 * source: everything comes from tiles already on screen.
 *
 * WATER IS THE INTERESTING CASE. It arrives as AREAS, not lines, but a wall is
 * a wall: we take each water polygon's RING as a line, so a lake shore joins the
 * road network as just another barrier and `polygonize` needs no special case.
 */

/** The OMT source + layers we read. */
const SOURCE = 'omt';
const TRANSPORTATION = 'transportation';
/** v25 BOLT-144: where road NAMES live. The geometry layer has none. */
const TRANSPORTATION_NAME = 'transportation_name';
const WATER_LAYERS = ['water', 'waterway'] as const;

function pushLine(out: BarrierLine[], seen: Set<string>, coords: number[][]): void {
  if (coords.length < 2) return;
  const key = JSON.stringify(coords);
  if (seen.has(key)) return; // adjacent tiles repeat features
  seen.add(key);
  out.push(lineString(coords) as BarrierLine);
}

/**
 * Every ring of a polygon or multipolygon, as barrier lines - MINUS the tile
 * rectangle.
 *
 * v25 BOLT-140: tiles CLIP area features, so an ocean or a wide river arrives as
 * a polygon whose ring runs along the tile edge wherever the water continues.
 * Feeding that to the arrangement makes a perfectly straight border out of
 * nothing at all: measured at 20N 150W, a 1.904 sq mi rectangle that scored
 * 100% on-barrier. `shorelineChains` keeps only the real shore.
 */
function pushRings(out: BarrierLine[], seen: Set<string>, geometry: Polygon | MultiPolygon): void {
  const polys = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  for (const rings of polys) for (const ring of rings) for (const chain of shorelineChains(ring)) pushLine(out, seen, chain);
}

function pushLines(out: BarrierLine[], seen: Set<string>, geometry: LineString | MultiLineString): void {
  if (geometry.type === 'LineString') pushLine(out, seen, geometry.coordinates);
  else for (const part of geometry.coordinates) pushLine(out, seen, part);
}



/** OMT road class -> the tier that owns it, derived from `TIER_CLASSES` so the
 *  two can never drift. */
const ROAD_TIER = new Map<string, BarrierKind>();
for (const tier of ['tertiary', 'secondary', 'primary'] as const) {
  for (const cls of TIER_CLASSES[tier]) ROAD_TIER.set(cls, tier);
}

/**
 * The harvest, keeping WHICH KIND of wall each line is.
 *
 * v25 BOLT-140: the selector's candidate ladder is a set of barrier kinds, so
 * it needs the tier of every line. This is the only place that knows it - by
 * the time a line is a bare `number[][]` the tier is gone - which is why the
 * harvest, not the geometry, is where classification lives.
 */
export function harvestClassified(map: MlMap, center: LngLat, radiusM: number): ClassifiedBarrier[] {
  return harvestWithNames(map, center, radiusM).barriers;
}

/**
 * v25 BOLT-144 — the harvest, plus the separate lines that carry NAMES.
 *
 * Road names are not on the road geometry. Verified against real MapTiler
 * tiles: the OpenMapTiles `transportation` layer has no `name` field at all
 * (Craig Ranch z14 - 298 road features, 0 named), because names live in a
 * parallel `transportation_name` layer drawn over the same roads. So the
 * composition label needs a SECOND set of lines, matched to the finished border
 * by proximity, and they must never reach the arrangement: handing it the same
 * road twice would double every wall and node the graph against itself.
 *
 * `waterway` is the exception that makes "Rowlett Creek" possible - it names
 * itself, so it is both a wall and a label.
 */
export function harvestWithNames(
  map: MlMap,
  center: LngLat,
  radiusM: number,
): { barriers: ClassifiedBarrier[]; names: NamedLine[] } {
  const barriers = harvestBarrierLines(map, center, radiusM);
  const names: NamedLine[] = [];
  const seen = new Set<string>();
  const pushName = (kind: BarrierKind, name: string, coords: number[][]) => {
    if (!name || coords.length < 2) return;
    const key = name + JSON.stringify(coords);
    if (seen.has(key)) return;
    seen.add(key);
    names.push({ kind, name, coordinates: coords });
  };

  if (map.getSource?.(SOURCE)) {
    let feats: GeoJSON.Feature[] = [];
    try {
      feats = map.querySourceFeatures(SOURCE, { sourceLayer: TRANSPORTATION_NAME });
    } catch {
      feats = [];
    }
    for (const f of feats) {
      const g = f.geometry;
      if (!g || (g.type !== 'LineString' && g.type !== 'MultiLineString')) continue;
      const cls = (f.properties?.class as string) ?? '';
      const kind = ROAD_TIER.get(cls) ?? (RAIL_CLASSES.has(cls) ? ('rail' as const) : undefined);
      if (!kind) continue;
      const name = (f.properties?.name as string) ?? '';
      if (g.type === 'LineString') pushName(kind, name, g.coordinates as number[][]);
      else for (const part of g.coordinates as number[][][]) pushName(kind, name, part);
    }
  }
  // A named waterway is a wall AND a label.
  for (const b of barriers) if (b.name) pushName(b.kind, b.name, b.coordinates);
  return { barriers, names };
}

function harvestBarrierLines(map: MlMap, center: LngLat, radiusM: number): ClassifiedBarrier[] {
  // No vector source means no classes to keep. The keyless lattice stays on the
  // single-set path (`harvestBarriers`), because tagging it by tier would give
  // three overlaid lattices at three spacings and change every keyless size.
  if (!map.getSource?.(SOURCE)) return [];

  const out: ClassifiedBarrier[] = [];
  const seen = new Set<string>();
  const push = (kind: BarrierKind, coords: number[][], name = '') => {
    if (coords.length < 2) return;
    const key = kind + JSON.stringify(coords);
    if (seen.has(key)) return; // adjacent tiles repeat features
    seen.add(key);
    out.push(name ? { kind, name, coordinates: coords } : { kind, coordinates: coords });
  };

  let roadFeats: GeoJSON.Feature[] = [];
  try {
    roadFeats = map.querySourceFeatures(SOURCE, { sourceLayer: TRANSPORTATION });
  } catch {
    roadFeats = [];
  }
  for (const f of roadFeats) {
    const g = f.geometry;
    if (!g || (g.type !== 'LineString' && g.type !== 'MultiLineString')) continue;
    const cls = (f.properties?.class as string) ?? '';
    const kind = ROAD_TIER.get(cls) ?? (RAIL_CLASSES.has(cls) ? ('rail' as const) : undefined);
    if (!kind) continue; // `minor`, service and path are the streets you play ON
    if (g.type === 'LineString') push(kind, g.coordinates as number[][]);
    else for (const part of g.coordinates as number[][][]) push(kind, part);
  }

  for (const layer of WATER_LAYERS) {
    let feats: GeoJSON.Feature[] = [];
    try {
      feats = map.querySourceFeatures(SOURCE, { sourceLayer: layer });
    } catch {
      continue;
    }
    for (const f of feats) {
      const g = f.geometry;
      if (!g) continue;
      // `isBarrierFeature` is still the one place that decides what water is a
      // wall: a suburb's `water` layer is mostly SWIMMING POOLS.
      if (!isBarrierFeature(layer, (f.properties?.class as string) ?? '', { roadClasses: new Set(), rail: false, water: true })) continue;
      // `waterway` names itself; the `water` polygon layer does not.
      const nm = layer === 'waterway' ? ((f.properties?.name as string) ?? '') : '';
      if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
        const polys = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
        for (const rings of polys) for (const ring of rings) for (const chain of shorelineChains(ring)) push('water', chain, nm);
      } else if (g.type === 'LineString') push('water', g.coordinates as number[][], nm);
      else if (g.type === 'MultiLineString') for (const part of g.coordinates as number[][][]) push('water', part, nm);
    }
  }
  return out;
}

/**
 * The keyless twin of the harvest (BOLT-124 #10): the fallback style has no
 * vector tiles at all, and the contract since v9 is that every flow stays fully
 * exercisable without imagery. A ~0.55 mi lattice puts the enclosed block
 * squarely inside foot's target range, so the algorithm produces a real face in
 * CI without a MapTiler key.
 */
export function syntheticBarriers(
  center: LngLat,
  barriers: readonly BarrierKind[],
  radiusM: number,
): BarrierLine[] {
  // Span well past the search window so the adaptive expansion still meets
  // real lattice lines instead of running out into open space.
  const [w, s, e, n] = windowBbox(center, radiusM * 3);
  // Spacing scales with the tier the checklist keeps, so unchecking a tier
  // genuinely produces a LARGER block, exactly as it does on real data.
  const tiers = barriers.filter((b) => b === 'tertiary' || b === 'secondary' || b === 'primary');
  // Chosen so each mode's enclosed block lands inside that mode's target range:
  // 1000 m ≈ 0.39 sq mi (foot), 1500 m ≈ 0.87 (bike), 2400 m ≈ 2.2 (car).
  const spacingM = tiers.includes('tertiary') ? 1000 : tiers.includes('secondary') ? 1500 : 2400;
  const stepLat = spacingM / 111_320;
  const stepLng = stepLat / Math.max(0.1, Math.cos((center.lat * Math.PI) / 180));

  const out: BarrierLine[] = [];
  const seen = new Set<string>();
  // Anchor the lattice ON the centre so the centre always sits inside a cell,
  // never on a line: a deterministic grid makes the tests deterministic too.
  for (let lat = center.lat - stepLat / 2; lat >= s; lat -= stepLat) pushLine(out, seen, [[w, lat], [e, lat]]);
  for (let lat = center.lat + stepLat / 2; lat <= n; lat += stepLat) pushLine(out, seen, [[w, lat], [e, lat]]);
  for (let lng = center.lng - stepLng / 2; lng >= w; lng -= stepLng) pushLine(out, seen, [[lng, s], [lng, n]]);
  for (let lng = center.lng + stepLng / 2; lng <= e; lng += stepLng) pushLine(out, seen, [[lng, s], [lng, n]]);
  return out;
}

/**
 * How far the harvested lines actually reach from `center`, in metres.
 *
 * v25: `querySourceFeatures` only returns features from LOADED tiles, so the
 * harvest silently inherits the viewport's extent. Measuring what we actually
 * got is the difference between "this map has no block that size" and "we never
 * looked that far" - and the second one used to masquerade as the first.
 */
/** A harvest within this fraction of the target counts as covered. */
export const COVERAGE_TOLERANCE = 0.9;


export function coverageRadiusM(lines: readonly BarrierLine[], center: LngLat): number {
  let max = 0;
  for (const line of lines) {
    for (const [lng, lat] of line.geometry.coordinates) {
      const dLat = (lat - center.lat) * 111_320;
      const dLng = (lng - center.lng) * 111_320 * Math.cos((center.lat * Math.PI) / 180);
      // The inscribed radius: a corner of the viewport does not prove the sides
      // reach that far, so take the limiting axis, not the diagonal.
      max = Math.max(max, Math.min(Math.abs(dLat), Math.abs(dLng)));
    }
  }
  return max;
}

export type { BarrierLine };
export type BarrierFeature = Feature<LineString>;
